import React, { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { changeSelectedPanelId, selectCreative } from 'store/pages/contentManagement/creativeSetDetails/reducer';
import framePortraitSvg from 'assets/icons/frame-portrait.svg';
import frameLandscapeSvg from 'assets/icons/frame-landscape.svg';
import { Store } from 'components/common/types/Store.types';
import Modal, { ModalSize } from 'lib/Modal';
import Button, { Color, Size } from 'lib/Button';
import { FilterDsp } from 'components/common/FilterArea/Filters';
import { CreativeType } from 'components/common/types';
import { MotionType } from 'consts/motionType';
import { getAdvertisers } from 'modules/api/content';
import Local from 'modules/Local';

import { cancelFunctions } from 'utils/cancelFunctions';
import { Advertiser, Brand } from 'components/common/types/Deal.types';
import { AsyncSelectLoadDataHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import CreativesTable from './CreativesTable';
import ContentSearchFilters from './ContentSearchFilters';
import { SelectedCreativeTypes, FilterTypes } from './AssignCreativeModal.types';

const AssignCreativeModal = (): React.ReactElement => {
  const [selectedCreative, setSelectedCreative] = useState<SelectedCreativeTypes>();
  const dispatch = useDispatch();

  const selectedPanelId = useSelector(
    (state: Store) => state.pages.contentManagement.creativeSetDetails.selectedPanelId,
  );
  const panels = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.panels);
  const dsp = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.dsp);
  const advertiser = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.advertiser);
  const brand = useSelector((state: Store) => state.pages.contentManagement.creativeSetDetails.brand);
  const panel = panels.find((p) => p.id === selectedPanelId);

  const isPortrait = (panel?.height ?? 0) > (panel?.width ?? 0);
  const orientation = isPortrait ? 'Portrait' : 'Landscape';

  const [filters, setFilters] = useState<FilterTypes>({
    dsp,
    advertiser,
    brand,
    creativeType: '',
  });

  useEffect(() => {
    setFilters({
      dsp,
      advertiser,
      brand,
      creativeType: panel?.motionType === MotionType.STILL ? CreativeType.BANNER : null,
    });
  }, [selectedPanelId]);

  const onLoadAdvertisersLookupData: AsyncSelectLoadDataHandler<Advertiser> = async (_, query) => {
    if (query.length < 2) return [];

    try {
      return await getAdvertisers(cancelFunctions, Local.getEnvironmentId(), query);
    } catch (error) {
      return [];
    }
  };

  const onLoadBrandLookupData = async (_: string, query: string): Promise<Brand[]> => {
    if (query.length < 2) return [];

    if (!filters.advertiser?.brands) return [];

    return filters.advertiser?.brands.filter(({ name }) => name.toLowerCase().includes(query.toLowerCase()));
  };

  const actionButtons = (
    <>
      <Button
        dataTestId="close-button"
        color={Color.DANGER}
        size={Size.LARGE}
        onClick={() => {
          dispatch(changeSelectedPanelId(''));
        }}
        label="Close"
      />
      <Button
        dataTestId="apply-button"
        color={Color.PRIMARY}
        size={Size.LARGE}
        onClick={() => {
          dispatch(
            selectCreative({
              creative: selectedCreative,
              ...selectedCreative,
            }),
          );
        }}
        isDisabled={!selectedCreative}
        label="Apply"
      />
    </>
  );

  return (
    <Modal
      dataTestId="assign-creative-modal"
      isOpen={!!selectedPanelId}
      icon={isPortrait ? framePortraitSvg : frameLandscapeSvg}
      title={panel ? `${orientation} ${panel.width}x${panel.height}` : ''}
      size={ModalSize.LARGE}
      actionButtons={actionButtons}
    >
      <div className="h-full flex flex-col">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 flex-1 mb-4">
          <FilterDsp
            dataTestId="dsp-select"
            selectedValue={filters.dsp}
            onChange={(_, value) => {
              setFilters((prevState) => ({ ...prevState, dsp: value }));
            }}
            isDisabled={!!dsp}
          />
          <ContentSearchFilters
            filters={filters}
            panelMotionType={panel?.motionType}
            advertiser={advertiser}
            brand={brand}
            onLoadAdvertisersLookupData={onLoadAdvertisersLookupData}
            onLoadBrandLookupData={onLoadBrandLookupData}
            setFilters={setFilters}
          />
        </div>
        <CreativesTable filters={filters} onSelectCreative={setSelectedCreative} panel={panel} />
      </div>
    </Modal>
  );
};

export default AssignCreativeModal;
